import React from "react";

import { styled } from '@mui/material/styles';

import Link from '@mui/material/Link';
import Typography from "@mui/material/Typography";

import { ArticleBase } from "./Layout";

const PREFIX = 'About';

const classes = {
  root: `${PREFIX}-root`,
  heading: `${PREFIX}-heading`,
  disclaimerTitle: `${PREFIX}-disclaimerTitle`,
  disclaimerText: `${PREFIX}-disclaimerText`,
  descriptionList: `${PREFIX}-descriptionList`,
  lineBreak: `${PREFIX}-lineBreak`
};

const StyledArticleBase = styled(ArticleBase)((
  {
    theme
  }
) => ({
  [`& .${classes.root}`]: {
    padding: theme.spacing(1)
  },

  [`& .${classes.heading}`]: {
    fontSize: theme.typography.pxToRem(19),
    fontWeight: 'bold'
  },

  [`& .${classes.disclaimerTitle}`]: {
    fontWeight: 'bold',
    fontSize: '0.9rem'
  },

  [`& .${classes.disclaimerText}`]: {
    fontSize: '0.9rem'
  },

  [`& .${classes.descriptionList}`]: {
    marginTop: 0
  },

  [`& .${classes.lineBreak}`]: {
    marginBottom: '15px'
  }
}));

const AboutContent = () => {

  return (
    <div className={classes.root}>
      <Typography component="p" paragraph>
        This website presents our COVID-19 prevalence estimates based on Google search data in the UK and England.
        The methodological approach together with broader findings for a number of different countries are presented in our paper
        titled <Link
        href="https://www.nature.com/articles/s41746-021-00384-w"
        underline="hover">Tracking COVID-19 using online search</Link> (npj Digital Medicine, 2021).
        We also provide estimates after reducing potential news media effects to the web search behaviour.
        A proxy indicator of news media coverage about COVID-19 in the UK (and England) is computed
        based on data from <Link
        href="https://open-platform.theguardian.com/documentation/"
        underline="hover">The Guardian</Link>.
        Our estimates are included in
        the <Link
        href="https://www.gov.uk/government/statistics/national-flu-and-covid-19-surveillance-reports-2021-to-2022-season"
        underline="hover">COVID-19
        syndromic surveillance reports</Link> produced by UK Health Security Agency (UKHSA).
        A lay summary of the methodology is provided
        in <Link
        href="https://npjdigitalmedcommunity.nature.com/posts/estimating-covid-19-prevalence-using-web-search-activity"
        underline="hover">this blog
        post</Link> and <Link
        href="https://twitter.com/lampos/status/1358796067773546498"
        underline="hover">this Twitter thread</Link>.
      </Typography>
      <Typography component="h3" className={classes.heading}>
        Research team
      </Typography>
      <dl className={classes.descriptionList}>
        <dt>
          <Typography component="span">
            <Link href="https://lampos.net" underline="hover">Vasileios Lampos</Link>
          </Typography>
        </dt>
        <dd>
          <Typography component="span">
            Associate Professor, UCL
          </Typography>
        </dd>
        <dt>
          <Typography component="span">
            Ingemar J. Cox
          </Typography>
        </dt>
        <dd>
          <Typography component="span">
            Professor in Information Retrieval, UCL
          </Typography>
        </dd>
        <dt>
          <Typography component="span">
            Aarzoo Dhiman
          </Typography>
        </dt>
        <dd>
          <Typography component="span">
            Research Fellow, UCL
          </Typography>
        </dd>
        <dt>
          <Typography component="span">
            David Guzman
          </Typography>
        </dt>
        <dd>
          <Typography component="span">
            Lead Software Engineer, UCL
          </Typography>
        </dd>
      </dl>
      <Typography component="h3" className={classes.heading}>
        Related publications
      </Typography>
      <Typography component="p" paragraph>
        Vasileios Lampos, Maimuna S. Majumder, Elad Yom-Tov, Michael Edelstein, Simon Moura,
        Yohhei Hamada, Molebogeng X. Rangaka, Rachel A. McKendry, Ingemar J. Cox
        (2021). <em><Link
        href="https://www.nature.com/articles/s41746-021-00384-w"
        underline="hover">Tracking COVID-19 using online search</Link></em>.
        npj Digital
        Medicine <strong>4</strong> (17). <strong>doi:</strong><Link href="https://doi.org/10.1038/s41746-021-00384-w" underline="hover">10.1038/s41746-021-00384-w</Link>
        <br className={classes.lineBreak}></br>
        Vasileios Lampos, Andrew Miller, Steve Crossan, Christian Stefansen
        (2015). <em><Link href="https://www.nature.com/articles/srep12760" underline="hover">Advances in nowcasting influenza-like illness rates using search query logs</Link></em>.
        Scientific Reports <strong>5</strong>
        (12760). <strong>doi:</strong><Link href="https://doi.org/10.1038/srep12760" underline="hover">10.1038/srep12760</Link>
      </Typography>
      <Typography component="h3" className={classes.heading}>
        News coverage
      </Typography>
      <Typography component="p" paragraph>
        <Link
          href="https://www.ucl.ac.uk/news/2021/feb/online-search-activity-can-help-predict-peaks-covid-19-cases"
          underline="hover">Online
        search activity can help predict peaks in COVID-19 cases</Link>, by Kate Corry, UCL News
        <br></br>
        <Link
          href="https://www.nytimes.com/2020/04/05/opinion/coronavirus-google-searches.html"
          underline="hover">Google Searches
              Can Help Us Find Emerging Covid-19 Outbreaks</Link>, by Seth Stephens-Davidowitz, New York Times
        <br></br>
        <Link
          href="https://www.huffingtonpost.co.uk/entry/coronavirus-google-search-data_uk_5f8d55d4c5b67da85d1fdead"
          underline="hover">How Your
              Google Search Data Is Used To Track Coronavirus</Link>, by Chris York, Huffington Post
        <br></br>
        <Link
          href="https://www.telegraph.co.uk/technology/2021/02/08/covid-outbreaks-could-spotted-weeks-early-tracking-google-searches/"
          underline="hover">Covid
              outbreaks could be spotted weeks early by tracking Google searches, study finds</Link>, by Hannah Boland, The Telegraph
        <br></br>
        <Link
          href="https://www.dailymail.co.uk/sciencetech/article-9237155/Online-searches-Covid-symptoms-predict-peaks-17-days-happen.html"
          underline="hover">'Hey
              Google, what are they symptoms of Covid-19?': Online searches about fever, loss of smell and skin rashes can predict peaks up to
              17 days before they happen, study reveals</Link>, by Jonathan Chadwick, Daily Mail
      </Typography>
      <br></br>
      <br></br>
      <Typography component="h3" className={classes.disclaimerTitle}>
        Disclaimer
      </Typography>
      <Typography component="p" className={classes.disclaimerText}>
        The information contained in this web site serves as a demonstration of research currently
        under way at University College London (UCL) and is provided 'as is' without warranty of
        any kind. Any reliance you place on this information is strictly at your own risk.
        COVID-19 Tracker is a demonstration of a research product and is used for research
        purposes only. We make no claim for its medical usability. UCL is not responsible for
        any use of this data. Through this web site you are able to link to other web sites
        which are not under our control. Therefore, the inclusion of any links does not
        necessarily imply a recommendation or endorses the view expressed within them. Access
        to this web site may be suspended temporarily and without any notice in circumstances
        of system failure or maintenance or for reasons beyond our control. We reserve the
        right to remove or alter the content of this web site at any time and without prior
        notice.
      </Typography>
    </div>
  );
};

const About = props => (
  <StyledArticleBase>
    <AboutContent />
  </StyledArticleBase>
);

export default About;
